import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Brain, Flame, HeartPulse, Hourglass, Moon, Sparkles, type LucideIcon } from "lucide-react";
import { container, gutters } from "@/components/ds";
import { formatUsd } from "@/components/ds/priceTagFormat";
import { useListings } from "@/hooks/useDatabase";
import { goalData } from "@/lib/goalData";
import { cn } from "@/lib/utils";
import { goalTileStats, listingsByCompound, MORE_GOALS, useHomeCompounds } from "@/components/home/homeData";
import { HomeHeading, homeSectionY, LateNumber, LinkRow } from "@/components/home/parts";
import HomeTools from "@/components/home/HomeTools";

/**
 * Homepage "Start with a goal or a tool": six research goals, the other goals as chips (in "Browse more" on a phone),
 * then the tools (HomeTools). Goals and tools share one section, so a phone pays for one header.
 *
 * The tiles are direction B's (the owner's pick of 2026-10-24): each goal has its own colour (.tone-* in index.css),
 * washed into the tile from the top corner, with a solid icon square that glows in the same colour. The colours mean
 * nothing beyond telling the goals apart; the evidence colours stay on the research benches.
 *
 * The numbers agree with the goal page each tile opens (goalTileStats): how many of the goal's compounds match a
 * compound in the table, and "from", the lowest of the prices GoalPage shows on those compounds' cards. Both lines
 * hold their height while the data loads.
 */
const PRIMARY: { slug: string; label: string; icon: LucideIcon; tone: string }[] = [
  { slug: "fat-loss", label: "Fat loss", icon: Flame, tone: "tone-orange" },
  { slug: "recovery", label: "Recovery", icon: HeartPulse, tone: "tone-green" },
  { slug: "focus", label: "Focus", icon: Brain, tone: "tone-violet" },
  { slug: "longevity", label: "Longevity", icon: Hourglass, tone: "tone-teal" },
  { slug: "sleep", label: "Sleep", icon: Moon, tone: "tone-indigo" },
  { slug: "skin-hair", label: "Skin and hair", icon: Sparkles, tone: "tone-pink" },
];

const MORE = MORE_GOALS.filter((g) => goalData.some((d) => d.slug === g.slug));

export default function HomeGoals() {
  const { data: compounds = [], isPending: compoundsPending } = useHomeCompounds();
  const { data: listings = [], isPending: listingsPending } = useListings();

  const stats = useMemo(() => {
    const byCompound = listingsByCompound(listings);
    const out: Record<string, { count: number; from: number | null }> = {};
    for (const { slug } of PRIMARY) {
      const goal = goalData.find((g) => g.slug === slug);
      if (goal) out[slug] = goalTileStats(goal.compounds, compounds, byCompound);
    }
    return out;
  }, [compounds, listings]);

  const countsLoading = compoundsPending && compounds.length === 0;
  const pricesLoading = countsLoading || (listingsPending && listings.length === 0);

  return (
    <section aria-labelledby="home-goals-title" className={cn(container.default, gutters, homeSectionY)}>
      <HomeHeading
        id="home-goals-title"
        title="Start with a goal or a tool"
        summary="Pick what you are researching and see the compounds studied for it."
        action={{ label: "All research benches", href: "/stack" }}
      />
      <ul className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-6">
        {PRIMARY.map(({ slug, label, icon: Icon, tone }) => {
          const s = stats[slug];
          return (
            <li key={slug} className="min-w-0">
              <Link
                to={`/goal/${slug}`}
                className={cn("tone-tile home-card-link group flex h-full flex-col p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-5", tone)}
              >
                <span aria-hidden className="tone-square flex h-11 w-11 items-center justify-center rounded-xl">
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </span>
                <span className="type-body mt-4 font-bold text-foreground group-hover:underline group-hover:underline-offset-4">{label}</span>
                <span className="type-small tabular-nums text-muted-foreground">
                  <LateNumber loading={countsLoading} value={s?.count ?? 0} placeholder="00" /> compounds
                </span>
                <span className="type-small tone-ink mt-3 font-bold tabular-nums">
                  <LateNumber loading={pricesLoading} value={s?.from ? `from ${formatUsd(s.from)}` : " "} placeholder="from $00.00" />
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
      {/* From 640px; on a phone these five goals are in "Browse more" at the end of the page. */}
      <LinkRow label="More goals" className="mt-6 hidden sm:flex" links={MORE.map((g) => ({ to: `/goal/${g.slug}`, label: g.label }))} />
      <HomeTools className="mt-10" />
    </section>
  );
}
